import { CheckOutlined } from '@mui/icons-material';
import { Box, TableCell, TableRow, Typography } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { getCanEditCountOfQty } from 'entities/Order/model/selectors/ordersPermissions';
import {
    getIsOrderSubmissionLocked,
    getSelectedOrderIsPreSubmission,
} from 'entities/Order/model/selectors/ordersSelectors';
import { Order } from 'entities/Order/model/types/OrderSchema';
import { getProposalQuantities } from 'entities/Proposal/lib/getProposalQuantities';
import { HydratedProposal } from 'entities/Proposal/model/types/ProposalSchema';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import { VendorActivationStatus } from 'entities/Vendors';
import {
    editOrderItemSourcingActions,
    getCanEditSuppliers,
    getTotalQuantity,
} from 'features/EditOrderItemSourcing';
import { formatNumber } from 'shared/lib/formatNumber/formatNumber';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { OrderItemQuantityPicker } from 'shared/ui/OrderItemQuantityPicker';
import { OrderPriceDisplay } from 'shared/ui/OrderPriceDisplay/OrderPriceDisplay';
import { SupplierLogo } from 'shared/ui/SupplierLogo';
import { SupplierStatusTooltip } from 'shared/ui/Suppliers';
import { Tooltip } from 'shared/ui/Tooltip';

import {
    proposalIsOutOfStock,
    proposalIsOverStock,
    proposalSupplierIsInRejected,
    proposalSupplierIsInVerifying,
} from '../../lib';
import { columnStyles } from '../OrderViewItemSupplierTable.styles';

interface OrderViewItemSupplierTableRowProps {
    proposal: HydratedProposal;
    selectedItem: CatalogItem;
    selectedOrder: Order;
    getSupplierQuantity: (proposalUid: string) => number;
    isUnselectedProposal?: boolean;
}

export const OrderViewItemSupplierTableRow = memo(
    ({
        proposal,
        selectedItem,
        selectedOrder,
        getSupplierQuantity,
        isUnselectedProposal,
    }: OrderViewItemSupplierTableRowProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const canEditSuppliers = useSelector(getCanEditSuppliers);
        const isPreSubmission = useSelector(getSelectedOrderIsPreSubmission);
        const totalQuantity = useSelector(getTotalQuantity);
        const canEditQty = useSelector(getCanEditCountOfQty);
        const canSeePrices = useSelector(getUserCanSeePrices);
        const isMaintenanceTechnician = useSelector(
            getUserIsMaintenanceTechnician,
        );
        const isOrderSubmissionLocked = useSelector(getIsOrderSubmissionLocked);

        const isOutOfStock =
            isPreSubmission && proposalIsOutOfStock(proposal, selectedItem);

        const isOverStock =
            isPreSubmission &&
            proposalIsOverStock(proposal, getSupplierQuantity);

        const isInVerifying =
            isPreSubmission && proposalSupplierIsInVerifying(proposal);

        const isInRejected =
            isPreSubmission && proposalSupplierIsInRejected(proposal);

        let supplierStatus: VendorActivationStatus | null = null;
        if (isInVerifying) {
            supplierStatus = VendorActivationStatus.VERIFYING;
        } else if (isInRejected) {
            supplierStatus = VendorActivationStatus.INVALID;
        }

        const itemPrice = isPreSubmission
            ? proposal.sellPrice
            : proposal.selectedProposal?.price ??
              (isUnselectedProposal ? proposal.sellPrice : undefined);

        const showPicker = isPreSubmission && canEditQty;
        const showPrices = canSeePrices && !isMaintenanceTechnician;

        const { approvedQty, unavailableQty } = getProposalQuantities(
            proposal.selectedProposal,
        );

        // Only mark as fully unavailable if there's unavailable quantity but NO available quantity
        const isSupplierFullyUnavailable =
            !isPreSubmission &&
            unavailableQty > 0 &&
            approvedQty === 0 &&
            (proposal.selectedProposal?.selectedQuantity ?? 0) > 0;

        const itemOpacity = isSupplierFullyUnavailable ? 0.35 : 1;

        // So the reason this exists is when we're in Item Details, we do not want to allow the user to decrease the quantity below 1 for the last allocation
        // They need to press the delete button instead
        // So we only set a 1 min quantity if the total quantity is 1 and the item quantity is 1
        const getMinQuantity = (uid: string) => {
            const itemQuantity = getSupplierQuantity(uid);
            if (totalQuantity === 1 && itemQuantity === 1) {
                return 1;
            }
            return 0;
        };

        return (
            <TableRow
                className={isOutOfStock ? 'out-of-stock' : ''}
                sx={{
                    backgroundColor: isSupplierFullyUnavailable
                        ? '#F7F7F7'
                        : 'transparent',
                    border: isSupplierFullyUnavailable
                        ? '1px solid #E9E8E8'
                        : 'none',
                    borderRadius: isSupplierFullyUnavailable ? '6px' : '0px',
                }}
            >
                <TableCell
                    className="table-suppliers"
                    sx={{ ...columnStyles.suppliers, opacity: itemOpacity }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '14px',
                        }}
                    >
                        <SupplierLogo
                            vendor={proposal.hydratedVendor}
                            size={26}
                        />
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: '1.4rem',
                                    fontWeight: 600,
                                    lineHeight: '2.4rem',
                                }}
                            >
                                {proposal.hydratedVendor?.title}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '7px',
                                }}
                            >
                                {supplierStatus && (
                                    <SupplierStatusTooltip
                                        status={supplierStatus}
                                    />
                                )}
                                {proposal.isPreferred && (
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            padding: '2px 9px 2px 6px',
                                            alignItems: 'center',
                                            gap: '3px',
                                            borderRadius: '14px',
                                            background: '#DBEFE1',
                                            color: '#009F3A',
                                            fontSize: '1rem',
                                            fontWeight: 700,
                                            marginTop: '4px',
                                        }}
                                    >
                                        <CheckOutlined
                                            sx={{
                                                fontSize: '1rem',
                                            }}
                                        />
                                        {t('Preferred')}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </TableCell>
                {showPrices && (
                    <TableCell
                        sx={{ ...columnStyles.unitCost, opacity: itemOpacity }}
                    >
                        <OrderPriceDisplay
                            price={itemPrice ?? null}
                            order={selectedOrder}
                            orderHasLoadingPrices={
                                !selectedItem.isCheckFinished
                            }
                            width="60px"
                            typography="body2"
                            containerProps={{
                                display: 'block',
                                textAlign: 'right',
                            }}
                            skeletonProps={{
                                margin: '0 auto',
                            }}
                        />
                    </TableCell>
                )}
                {isPreSubmission && (
                    <TableCell sx={columnStyles.stock}>
                        {isOutOfStock ? (
                            <Typography sx={{ color: '#F00' }} variant="body2">
                                {t('Out')}
                            </Typography>
                        ) : (
                            formatNumber(proposal.availableQuantity)
                        )}
                    </TableCell>
                )}
                {!isPreSubmission && !isUnselectedProposal && (
                    <TableCell
                        sx={{
                            width: '80px',
                            paddingLeft: '0px',
                            paddingRight: '15px',
                            textAlign: 'right',
                            color: '#F00',
                        }}
                    >
                        {unavailableQty > 0 ? unavailableQty : ''}
                    </TableCell>
                )}
                {!isPreSubmission && !isUnselectedProposal && (
                    <TableCell
                        sx={{
                            width: '80px',
                            paddingLeft: '0px',
                            paddingRight: '15px',
                            textAlign: 'right',
                        }}
                    >
                        {approvedQty > 0 ? approvedQty : ''}
                    </TableCell>
                )}
                {isPreSubmission && (
                    <TableCell sx={columnStyles.qty}>
                        {isOverStock && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '-30px',
                                    transform: 'translateY(-12px)',
                                }}
                            >
                                <Tooltip
                                    isError
                                    content={t(
                                        'Requested quantity exceeds available stock.',
                                    )}
                                />
                            </Box>
                        )}
                        {showPicker ? (
                            <OrderItemQuantityPicker
                                value={getSupplierQuantity(proposal.uid)}
                                maxQuantity={proposal.availableQuantity}
                                minQuantity={getMinQuantity(proposal.uid)}
                                onQuantityChange={(newQuantity) => {
                                    dispatch(
                                        editOrderItemSourcingActions.updateSupplierQuantity(
                                            {
                                                proposalUid: proposal.uid,
                                                quantity: newQuantity,
                                            },
                                        ),
                                    );
                                }}
                                disabled={
                                    !canEditSuppliers || isOrderSubmissionLocked
                                }
                            />
                        ) : (
                            proposal.selectedProposal?.selectedQuantity ?? 0
                        )}
                    </TableCell>
                )}
                {showPrices && !isUnselectedProposal && (
                    <TableCell
                        sx={{ ...columnStyles.total, opacity: itemOpacity }}
                    >
                        {isPreSubmission ? (
                            getSupplierQuantity(proposal.uid) > 0 && (
                                <OrderPriceDisplay
                                    price={
                                        getSupplierQuantity(proposal.uid) *
                                        itemPrice
                                    }
                                    order={selectedOrder}
                                    orderHasLoadingPrices={
                                        !selectedItem.isCheckFinished
                                    }
                                    typography="subtitle2"
                                    justifyContent="flex-end"
                                    width="60px"
                                />
                            )
                        ) : (
                            <OrderPriceDisplay
                                price={
                                    isSupplierFullyUnavailable &&
                                    approvedQty === 0
                                        ? 0
                                        : approvedQty * (itemPrice ?? 0)
                                }
                                order={selectedOrder}
                                orderHasLoadingPrices={
                                    !selectedItem.isCheckFinished
                                }
                                typography="subtitle2"
                                justifyContent="flex-end"
                                width="60px"
                                suppressEmptyPrice={
                                    !(
                                        isSupplierFullyUnavailable &&
                                        approvedQty === 0
                                    )
                                }
                                showZeroAsPrice={
                                    isSupplierFullyUnavailable &&
                                    approvedQty === 0
                                }
                            />
                        )}
                    </TableCell>
                )}
                {isUnselectedProposal && (
                    <TableCell sx={{ ...columnStyles.total }}>
                        {proposal.availableQuantity}
                    </TableCell>
                )}
            </TableRow>
        );
    },
);
